import { useCallback, useEffect, useState } from 'react'
import {
  ALAN_ADI,
  EGZERSIZ_ADI,
  KANAL_ADI,
  RANDEVU_DURUMU,
  api,
  type Cocuk,
  type DavranisHedefi,
  type FormIstegi,
  type FormTanimi,
  type Odev,
  type RaporOzet,
  type Randevu,
} from '../api'
import { GozlemFormu } from './Form'
import { Davranislar } from './Davranis'

function gun(s: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('tr-TR', { dateStyle: 'medium' })
}

function gunSaat(s: string): string {
  const d = new Date(s)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('tr-TR', { dateStyle: 'full', timeStyle: 'short' })
}

/** Bugün dahil, son tarihi geçmiş ve yapılmamış ödev */
function gecikmis(o: Odev): boolean {
  if (o.yapildi || !o.sonTarih) return false
  const bugun = new Date()
  bugun.setHours(0, 0, 0, 0)
  return new Date(o.sonTarih) < bugun
}

export function CocukSayfasi({
  cocuk,
  onRapor,
}: {
  cocuk: Cocuk
  onRapor: (raporId: string) => void
}) {
  const [odevler, setOdevler] = useState<Odev[] | null>(null)
  const [randevular, setRandevular] = useState<Randevu[]>([])
  const [raporlar, setRaporlar] = useState<RaporOzet[]>([])
  const [formlar, setFormlar] = useState<FormIstegi[]>([])
  const [tanimlar, setTanimlar] = useState<Record<string, FormTanimi>>({})
  const [dolduruluyor, setDolduruluyor] = useState<FormIstegi | null>(null)
  const [formBildirimi, setFormBildirimi] = useState<string | null>(null)
  const [davranislar, setDavranislar] = useState<DavranisHedefi[]>([])
  const [buHafta, setBuHafta] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [isaretlenen, setIsaretlenen] = useState<string | null>(null)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      const [o, r, p, f, dv] = await Promise.all([
        api.odevler(cocuk.id),
        api.randevular(cocuk.id),
        api.raporlar(cocuk.id),
        api.formlar(cocuk.id),
        api.davranislar(cocuk.id),
      ])
      setOdevler(o.odevler)
      setRandevular(r.randevular)
      setRaporlar(p.raporlar)
      setFormlar(f.formlar)
      setTanimlar(f.tanimlar)
      setDavranislar(dv.hedefler)
      setBuHafta(dv.buHafta)
    } catch {
      setHata('Bilgiler alınamadı. Bağlantınızı kontrol edip tekrar deneyin.')
      setOdevler([])
    }
  }, [cocuk.id])

  useEffect(() => {
    void yukle()
  }, [yukle])

  /**
   * Sekme öne gelince tazele.
   *
   * Yükleyici yalnızca değişmeyen bir kimliğe bağlı; ilk açılışta bir
   * kez koşuyor ve ekran açık kaldığı sürece bir daha hiç veri
   * çekmiyordu. Bu ekranlar tam da açık bırakılan ekranlar: veli
   * telefonunda sekmeyi açık bırakıyor, uygulayıcı o sırada ödev
   * tanımlıyor ya da rapor gönderiyor, veli hâlâ "henüz bir şey yok"
   * görüyor.
   *
   * Aralıklı yoklama YAPILMIYOR: sunucuda hız sınırı var ve kimsenin
   * bakmadığı bir sekme için saatlerce istek atmak doğru değil.
   * Tazeleme, kullanıcı ekrana GERİ DÖNDÜĞÜNDE — yani bayatlığın
   * önem kazandığı anda — bir kez oluyor.
   */
  useEffect(() => {
    const geriDon = () => {
      if (document.visibilityState === 'visible') void yukle()
    }
    document.addEventListener('visibilitychange', geriDon)
    window.addEventListener('focus', geriDon)
    return () => {
      document.removeEventListener('visibilitychange', geriDon)
      window.removeEventListener('focus', geriDon)
    }
  }, [yukle])

  const yapildi = async (odev: Odev) => {
    setIsaretlenen(odev.id)
    try {
      await api.odevYapildi(odev.id)
      await yukle()
    } catch {
      setHata('İşaretlenemedi. Tablet egzersizleri kendiliğinden işaretlenir.')
    } finally {
      setIsaretlenen(null)
    }
  }

  const bekleyen = (odevler ?? []).filter((o) => !o.yapildi)
  const biten = (odevler ?? []).filter((o) => o.yapildi)
  const siradaki = randevular.find(
    (r) => r.durum === 'scheduled' && new Date(r.baslangic) > new Date(),
  )

  if (dolduruluyor) {
    const tanim = tanimlar[dolduruluyor.kod]
    return !tanim ? (
      <div className="uyari">Form tanımı yüklenemedi. Sayfayı yenileyin.</div>
    ) : (
      <GozlemFormu
        tanim={tanim}
        not={dolduruluyor.not}
        onGonder={async (cevaplar) => {
          await api.formGonder(dolduruluyor.id, cevaplar)
          setDolduruluyor(null)
          setFormBildirimi('Form gönderildi. Teşekkürler.')
          await yukle()
        }}
        onIptal={() => setDolduruluyor(null)}
      />
    )
  }

  return (
    <>
      <h1>{cocuk.ad}</h1>
      <p className="alt">
        Haftalık takip, ödevler, formlar, randevular ve size gönderilen raporlar.
      </p>

      {hata && <div className="uyari">{hata}</div>}

      {siradaki && (
        <div className="bilgi">
          <strong>Sıradaki randevu:</strong> {gunSaat(siradaki.baslangic)}
          {siradaki.uygulayici ? ` · ${siradaki.uygulayici}` : ''}
        </div>
      )}

      {formBildirimi && <div className="bilgi">{formBildirimi}</div>}

      {formlar.length > 0 && (
        <>
          <h2>Sizden istenen formlar</h2>
          <p className="alt">
            Doğru ya da yanlış cevap yok ve bir puan gösterilmiyor. Uygulayıcı, cihazdaki
            ölçümün göremediğini — evde nasıl olduğunu — sizden öğreniyor.
          </p>
          {formlar.map((f) => (
            <div
              className="kart"
              key={f.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div>
                <div>{tanimlar[f.kod]?.ad ?? 'Gözlem formu'}</div>
                <p className="alt" style={{ margin: 0 }}>
                  İstendi: {gun(f.istendi)}
                </p>
              </div>
              <button className="birincil" onClick={() => setDolduruluyor(f)}>
                Doldur
              </button>
            </div>
          ))}
        </>
      )}

      {buHafta && (
        <Davranislar
          cocukId={cocuk.id}
          hedefler={davranislar}
          buHafta={buHafta}
          onKaydedildi={() => void yukle()}
        />
      )}

      <h2>Ödevler</h2>
      {odevler === null ? (
        <p className="alt">Yükleniyor…</p>
      ) : odevler.length === 0 ? (
        <div className="bilgi">
          Şu anda tanımlı ödev yok. Program başladığında burada görünecek.
        </div>
      ) : (
        <>
          {bekleyen.length === 0 ? (
            <div className="kart" style={{ borderColor: 'var(--ok)' }}>
              Bu haftanın ödevlerinin hepsi yapılmış. Eline sağlık.
            </div>
          ) : (
            bekleyen.map((o) => (
              <div className="kart" key={o.id} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <strong>{EGZERSIZ_ADI[o.kod] ?? o.kod}</strong>
                    <div className="alt" style={{ margin: '2px 0 0' }}>
                      {ALAN_ADI[o.alan] ?? o.alan} · {KANAL_ADI[o.kanal] ?? o.kanal} ·{' '}
                      {o.hafta}. hafta
                      {o.sonTarih ? ` · son gün ${gun(o.sonTarih)}` : ''}
                    </div>
                  </div>
                  {gecikmis(o) && <span className="rozet warn">gecikti</span>}
                  {o.kanal === 'digital' ? (
                    <span className="alt" style={{ margin: 0 }}>
                      tablette yapılacak
                    </span>
                  ) : (
                    <button
                      className="birincil"
                      disabled={isaretlenen === o.id}
                      onClick={() => void yapildi(o)}
                    >
                      {isaretlenen === o.id ? 'İşaretleniyor…' : 'Yapıldı'}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}

          {biten.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', color: 'var(--muted)', fontSize: 14 }}>
                Yapılanlar ({biten.length})
              </summary>
              <table style={{ marginTop: 10 }}>
                <thead>
                  <tr>
                    <th>Egzersiz</th>
                    <th>Hafta</th>
                    <th>Yapıldığı gün</th>
                  </tr>
                </thead>
                <tbody>
                  {biten.map((o) => (
                    <tr key={o.id}>
                      <td>{EGZERSIZ_ADI[o.kod] ?? o.kod}</td>
                      <td>{o.hafta}</td>
                      <td style={{ color: 'var(--muted)' }}>{gun(o.yapildi)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </>
      )}

      <h2>Randevular</h2>
      {randevular.length === 0 ? (
        <div className="bilgi">Planlanmış randevu görünmüyor.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Tarih</th>
              <th>Uygulayıcı</th>
              <th>Durum</th>
            </tr>
          </thead>
          <tbody>
            {randevular.map((r) => (
              <tr key={r.id}>
                <td>{gunSaat(r.baslangic)}</td>
                <td>{r.uygulayici ?? '—'}</td>
                <td>
                  <span
                    className={
                      'rozet' +
                      (r.durum === 'attended' ? ' ok' : r.durum === 'cancelled' ? ' bad' : '')
                    }
                  >
                    {RANDEVU_DURUMU[r.durum] ?? r.durum}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Raporlar</h2>
      {raporlar.length === 0 ? (
        <div className="bilgi">
          Henüz size gönderilmiş bir rapor yok. Uygulayıcı gönderdiğinde burada görünür.
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Dönem</th>
              <th>Gönderildiği gün</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {raporlar.map((r) => (
              <tr key={r.id}>
                <td>
                  {gun(r.donemBas)} – {gun(r.donemBit)}
                </td>
                <td style={{ color: 'var(--muted)' }}>{gun(r.gonderildi)}</td>
                <td style={{ textAlign: 'right' }}>
                  <button onClick={() => onRapor(r.id)}>Aç</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}
